import { makeAutoObservable } from "mobx";
import RootStore from "stores/RootStore";

// Types
export interface IMaterialBin {
  id: string;
  name: string;
  type: 'aggregate' | 'cement' | 'water' | 'admixture';
  currentLevel: number;
  capacity: number;
  targetWeight: number;
  actualWeight: number;
  color: string;
  status: 'active' | 'idle' | 'maintenance';
}

export interface IEquipment {
  id: string;
  name: string;
  type: 'conveyor' | 'mixer' | 'dust_collector' | 'control_building';
  status: 'active' | 'idle' | 'maintenance';
  isRunning?: boolean;
  mixerLevel?: number;
  isRotating?: boolean;
  isDischarging?: boolean;
}

export interface ITruckBay {
  id: string;
  truckNumber: string | null;
  truckPresent: boolean;
  loadLevel: number;
  status: 'loading' | 'waiting' | 'empty';
  ticketNumber?: string;
  mixDesign?: string;
  customerName?: string;
}

export interface IUpcomingTruck {
  id: string;
  truckNumber: string;
  ticketNumber: string;
  mixDesign: string;
  volume: number;
  customerName: string;
  estimatedTime: string;
}

export type PlantMode = 'idle' | 'batching' | 'loading' | 'complete';

// Mock data - Material Bins
const MOCK_MATERIAL_BINS: IMaterialBin[] = [
  // Aggregates
  { id: 'agg1', name: '3/4"', type: 'aggregate', currentLevel: 67, capacity: 100, targetWeight: 1565, actualWeight: 0, color: '#c4a35a', status: 'active' },
  { id: 'agg2', name: '3/8"', type: 'aggregate', currentLevel: 51, capacity: 100, targetWeight: 996, actualWeight: 0, color: '#b89a4d', status: 'active' },
  { id: 'agg3', name: 'Sand', type: 'aggregate', currentLevel: 70, capacity: 100, targetWeight: 998, actualWeight: 0, color: '#d4b86a', status: 'active' },
  { id: 'agg4', name: 'Fine', type: 'aggregate', currentLevel: 79, capacity: 100, targetWeight: 996, actualWeight: 0, color: '#e8cc7a', status: 'idle' },
  // Cement
  { id: 'cem1', name: 'Type-10', type: 'cement', currentLevel: 71, capacity: 100, targetWeight: 386, actualWeight: 0, color: '#8a9a8a', status: 'active' },
  { id: 'cem2', name: 'Fly Ash', type: 'cement', currentLevel: 68, capacity: 100, targetWeight: 115, actualWeight: 0, color: '#6a7a6a', status: 'active' },
  { id: 'cem3', name: 'Slag', type: 'cement', currentLevel: 74, capacity: 100, targetWeight: 0, actualWeight: 0, color: '#5a6a5a', status: 'idle' },
  // Water
  { id: 'water1', name: 'Hot', type: 'water', currentLevel: 71, capacity: 100, targetWeight: 145, actualWeight: 0, color: '#ff6b6b', status: 'active' },
  { id: 'water2', name: 'Cold', type: 'water', currentLevel: 56, capacity: 100, targetWeight: 0, actualWeight: 0, color: '#4dabf7', status: 'active' },
  // Admixtures
  { id: 'adm1', name: 'AEA', type: 'admixture', currentLevel: 51, capacity: 100, targetWeight: 12, actualWeight: 0, color: '#ffd700', status: 'active' },
  { id: 'adm2', name: 'Retarder', type: 'admixture', currentLevel: 80, capacity: 100, targetWeight: 8, actualWeight: 0, color: '#2196f3', status: 'active' },
  { id: 'adm3', name: 'Super-P', type: 'admixture', currentLevel: 68, capacity: 100, targetWeight: 15, actualWeight: 0, color: '#4caf50', status: 'maintenance' },
];

// Mock data - Equipment
const MOCK_EQUIPMENT: IEquipment[] = [
  { id: 'conveyor1', name: 'Main Conveyor', type: 'conveyor', status: 'active', isRunning: true },
  { id: 'mixer1', name: 'Central Mixer', type: 'mixer', status: 'active', mixerLevel: 45, isRotating: true, isDischarging: false },
  { id: 'dust1', name: 'Dust Collector', type: 'dust_collector', status: 'active' },
  { id: 'control1', name: 'Control Room', type: 'control_building', status: 'active' },
];

// Mock data - Truck Bay
const MOCK_TRUCK_BAY: ITruckBay = {
  id: 'bay1',
  truckNumber: '102',
  truckPresent: true,
  loadLevel: 35,
  status: 'loading',
  ticketNumber: 'T-1001',
  mixDesign: '3000AE',
  customerName: 'ABC Construction',
};

// Mock data - Upcoming Trucks
const MOCK_UPCOMING_TRUCKS: IUpcomingTruck[] = [
  { id: 'up1', truckNumber: '105', ticketNumber: 'T-1002', mixDesign: '4000AE', volume: 8, customerName: 'XYZ Builders', estimatedTime: '10:15 AM' },
  { id: 'up2', truckNumber: '108', ticketNumber: 'T-1003', mixDesign: '3000AE', volume: 10, customerName: 'ABC Construction', estimatedTime: '10:30 AM' },
  { id: 'up3', truckNumber: '103', ticketNumber: 'T-1004', mixDesign: '5000', volume: 6, customerName: 'Metro Concrete', estimatedTime: '10:45 AM' },
  { id: 'up4', truckNumber: '110', ticketNumber: 'T-1005', mixDesign: '3500AE', volume: 9, customerName: 'City Works', estimatedTime: '11:00 AM' },
];

class PlantOverviewStore {
  rootStore: RootStore;

  // Data
  materialBins: IMaterialBin[] = [...MOCK_MATERIAL_BINS];
  equipment: IEquipment[] = [...MOCK_EQUIPMENT];
  truckBay: ITruckBay = { ...MOCK_TRUCK_BAY };
  upcomingTrucks: IUpcomingTruck[] = [...MOCK_UPCOMING_TRUCKS];
  plantMode: PlantMode = 'loading';

  // UI State
  selectedEquipmentId: string | null = null;
  selectedEquipmentType: 'bin' | 'equipment' | 'truck' | null = null;
  loading = false;
  error: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Computed: Filtered bins by type
  get aggregateBins(): IMaterialBin[] {
    return this.materialBins.filter(b => b.type === 'aggregate');
  }

  get cementBins(): IMaterialBin[] {
    return this.materialBins.filter(b => b.type === 'cement');
  }

  get waterBins(): IMaterialBin[] {
    return this.materialBins.filter(b => b.type === 'water');
  }

  get admixtureBins(): IMaterialBin[] {
    return this.materialBins.filter(b => b.type === 'admixture');
  }

  // Computed: Equipment by type
  get conveyor(): IEquipment | undefined {
    return this.equipment.find(e => e.type === 'conveyor');
  }

  get mixer(): IEquipment | undefined {
    return this.equipment.find(e => e.type === 'mixer');
  }

  get dustCollector(): IEquipment | undefined {
    return this.equipment.find(e => e.type === 'dust_collector');
  }

  get controlBuilding(): IEquipment | undefined {
    return this.equipment.find(e => e.type === 'control_building');
  }

  // Computed: Selected item
  get selectedBin(): IMaterialBin | null {
    if (!this.selectedEquipmentId || this.selectedEquipmentType !== 'bin') return null;
    return this.materialBins.find(b => b.id === this.selectedEquipmentId) || null;
  }

  get selectedEquipment(): IEquipment | null {
    if (!this.selectedEquipmentId || this.selectedEquipmentType !== 'equipment') return null;
    return this.equipment.find(e => e.id === this.selectedEquipmentId) || null;
  }

  get selectedTruck(): ITruckBay | null {
    if (!this.selectedEquipmentId || this.selectedEquipmentType !== 'truck') return null;
    return this.truckBay.id === this.selectedEquipmentId ? this.truckBay : null;
  }

  // Computed: Aggregated stats
  get totalStorageUtilization(): number {
    if (this.materialBins.length === 0) return 0;
    const total = this.materialBins.reduce((sum, b) => sum + b.currentLevel, 0);
    return total / this.materialBins.length;
  }

  get activeEquipmentCount(): number {
    return this.equipment.filter(e => e.status === 'active').length;
  }

  get totalEquipmentCount(): number {
    return this.equipment.length;
  }

  get activeBinsCount(): number {
    return this.materialBins.filter(b => b.status === 'active').length;
  }

  get totalBinsCount(): number {
    return this.materialBins.length;
  }

  // Helper: Get utilization color
  getUtilizationColor = (percent: number): string => {
    if (percent >= 75) return '#4caf50';
    if (percent >= 50) return '#ff9800';
    if (percent >= 25) return '#2196f3';
    return '#9e9e9e';
  };

  // Helper: Get status color
  getStatusColor = (status: string): string => {
    switch (status) {
      case 'active': return '#4caf50';
      case 'idle': return '#ff9800';
      case 'maintenance': return '#f44336';
      case 'loading': return '#2196f3';
      case 'waiting': return '#ff9800';
      case 'empty': return '#9e9e9e';
      default: return '#9e9e9e';
    }
  };

  // Helper: Get plant mode color
  getPlantModeColor = (): string => {
    switch (this.plantMode) {
      case 'batching': return '#2196f3';
      case 'loading': return '#4caf50';
      case 'complete': return '#9c27b0';
      case 'idle':
      default: return '#9e9e9e';
    }
  };

  // Actions
  selectBin = (binId: string | null) => {
    this.selectedEquipmentId = binId;
    this.selectedEquipmentType = binId ? 'bin' : null;
  };

  selectEquipment = (equipmentId: string | null) => {
    this.selectedEquipmentId = equipmentId;
    this.selectedEquipmentType = equipmentId ? 'equipment' : null;
  };

  selectTruck = (truckId: string | null) => {
    this.selectedEquipmentId = truckId;
    this.selectedEquipmentType = truckId ? 'truck' : null;
  };

  clearSelection = () => {
    this.selectedEquipmentId = null;
    this.selectedEquipmentType = null;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  reset = () => {
    this.selectedEquipmentId = null;
    this.selectedEquipmentType = null;
    this.loading = false;
    this.error = null;
  };
}

export default PlantOverviewStore;
